if (!customElements.get('sticky-atc')) {
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      // The theme's product-info.js / product-form.js also load deferred, so
      // the form may not be in the DOM when this custom element upgrades.
      // Wait for it (poll briefly) before initializing instead of bailing.
      this.initAttempts = 0;
      try {
        this.waitForPdpThenInit();
      } catch (e) {
        console.error('Sticky ATC: init failed', e);
      }
    }

    waitForPdpThenInit() {
      const customPdp = document.querySelector('.bea-cdpdp, .bea-aiopdp');
      const productInfo = document.querySelector('product-info[id^="MainProduct-"]')
                       || document.querySelector('product-info');
      const mainForm = this.findMainForm();

      const ready = customPdp || mainForm || productInfo;

      if (ready) {
        this.init();
        return;
      }

      // Not ready yet — retry for up to ~3s (30 × 100ms), then give up quietly.
      this.initAttempts++;
      if (this.initAttempts <= 30) {
        setTimeout(() => {
          try {
            this.waitForPdpThenInit();
          } catch (e) {
            console.error('Sticky ATC: init failed', e);
          }
        }, 100);
      } else {
        console.warn('Sticky ATC: no compatible PDP section found after waiting');
      }
    }

    init() {
      // ── PDP mode detection ──
      this.customPdp = document.querySelector('.bea-cdpdp, .bea-aiopdp');
      this.productInfo = document.querySelector('product-info[id^="MainProduct-"]')
                      || document.querySelector('product-info');
      this.mainForm = this.findMainForm();

      if (this.customPdp) {
        this.mode = 'custom';
        this.initCustomMode();
      } else if (this.mainForm || this.productInfo) {
        this.mode = 'dawn';
        this.initDawnMode();
      } else {
        console.warn('Sticky ATC: no compatible PDP section found');
        return;
      }

      // Sticky bar own elements
      this.variantSelect = this.querySelector('[data-sticky-variant-select]');
      this.priceEl       = this.querySelector('[data-sticky-price]');
      this.addButton     = this.querySelector('[data-sticky-add-button]');
      this.buttonText    = this.querySelector('[data-sticky-button-text]');
      this.quantityInput = this.querySelector('[data-sticky-quantity-input]');
      this.quantityMinus = this.querySelector('[data-sticky-quantity-minus]');
      this.quantityPlus  = this.querySelector('[data-sticky-quantity-plus]');

      // Header
      this.sectionHeader = this.resolveHeaderEl();

      // Labels
      this.addLabel       = this.dataset.addLabel       || 'Agregar al carrito';
      this.addLabelMobile = this.dataset.addLabelMobile || 'Agregar';
      this.soldOutLabel   = this.dataset.soldOutLabel   || 'Agotado';

      if (!this.triggerEl) {
        console.warn('Sticky ATC: no trigger element found');
        return;
      }

      this.dataset.visible = 'false';
      this.removeAttribute('hidden');

      this.setupHeaderOffsetSync();
      this.setupVisibilityLogic();
      this.setupVariantSync();
      this.setupQuantityStepper();
      this.setupAddToCart();
      this.listenForMainFormChanges();

      this.evaluateVisibility();
    }

    disconnectedCallback() {
      if (this.observer)         this.observer.disconnect();
      if (this.mutationObserver) this.mutationObserver.disconnect();
      if (this.headerObserver)   this.headerObserver.disconnect();
      if (this.boundScrollHandler) {
        window.removeEventListener('scroll', this.boundScrollHandler);
        window.removeEventListener('resize', this.boundScrollHandler);
      }
    }

    /* ═══════════════ FORM HELPERS ═══════════════ */

    // IMPORTANT: never read `form.id` directly.
    // HTMLFormElement uses [LegacyOverrideBuiltIns], so a control named "id"
    // (Dawn's <input type="hidden" name="id">) shadows the element's own
    // id property and returns the INPUT ELEMENT instead of a string.
    // Always go through getAttribute('id').
    getFormId(form) {
      return (form && form.getAttribute('id')) || '';
    }

    findMainForm() {
      const scope = document.querySelector('product-info') || document;
      const forms = scope.querySelectorAll('form[action*="/cart/add"], form[id^="product-form-"]');
      for (const f of forms) {
        const id = this.getFormId(f);
        if (id.startsWith('product-form-installment-')) continue;
        if (f.querySelector('button[name="add"], [name="add"]')) return f;
      }
      for (const f of forms) {
        const id = this.getFormId(f);
        if (id.startsWith('product-form-installment-')) continue;
        return f;
      }
      return null;
    }

    /* ═══════════════ MODE INITIALIZERS ═══════════════ */

    initCustomMode() {
      const pdp = this.customPdp;
      // Supports both BEA heroes: Core Disc (`bea-cdpdp`) and Core All in One Kit (`bea-aiopdp`).
      const px = pdp.classList.contains('bea-aiopdp') ? 'bea-aiopdp' : 'bea-cdpdp';
      this.pdpPrefix = px;
      this.mainForm      = pdp.querySelector('form.' + px + '__form')
                        || pdp.querySelector('form[action*="/cart/add"]');
      this.mainCta       = pdp.querySelector('[data-' + px + '-cta]');
      this.mainVariantId = pdp.querySelector('[data-' + px + '-variant-id]');
      this.mainRadios    = pdp.querySelectorAll('input.' + px + '__variant-input');
      this.variantGroups = pdp.querySelectorAll('.' + px + '__variants');
      this.triggerEl     = pdp.querySelector('.' + px + '__info') || pdp;

      const variantsScript = pdp.querySelector('[data-' + px + '-variants]');
      try { this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : []; }
      catch (e) { this.variants = []; }
    }

    initDawnMode() {
      if (!this.mainForm) this.mainForm = this.findMainForm();

      this.mainCta       = this.mainForm ? this.mainForm.querySelector('button[name="add"]') : null;
      this.mainVariantId = this.mainForm ? this.mainForm.querySelector('input[name="id"]') : null;

      const formId = this.getFormId(this.mainForm);
      this.dawnSectionId = formId.replace(/^product-form-/, '');

      this.triggerEl =
        (this.productInfo && this.productInfo.querySelector('.product__info-wrapper')) ||
        (this.productInfo && this.productInfo.querySelector('.product__info-container')) ||
        document.querySelector('.product__info-wrapper') ||
        document.querySelector('.product__info-container') ||
        this.productInfo ||
        (this.mainForm ? this.mainForm.closest('.product__info-wrapper, .product__info-container') : null);

      this.variants = [];
    }

    /* ═══════════════ HEADER OFFSET ═══════════════ */

    // NOTE: querySelector with a comma list returns the first match in DOCUMENT
    // ORDER, not the first selector that matches — so `.section-header` (a
    // collapsed wrapper whose child is position:fixed) used to win over the
    // element that is actually pinned. Query each candidate separately and
    // prefer whichever is genuinely fixed/sticky.
    resolveHeaderEl() {
      const candidates = [
        '.bea-mm-always-sticky',        // BEA megamenu, always-sticky mode
        '.bea-mm-sticky-target',        // BEA megamenu, scroll-up mode
        'bea-megamenu.bea-mm',          // BEA megamenu, standalone root
        'sticky-header',
        '.header-wrapper',
        'header.header',
        '.shopify-section-header-sticky',
        '.section-header'
      ];

      let fallback = null;
      for (const sel of candidates) {
        const el = document.querySelector(sel);
        if (!el) continue;
        if (!fallback) fallback = el;
        const pos = getComputedStyle(el).position;
        if (pos === 'fixed' || pos === 'sticky') return el;
      }
      return fallback;
    }

    getHeaderBottom() {
      // The BEA megamenu adds its sticky class on DOMContentLoaded, which is
      // AFTER this deferred script runs — so the header element can change
      // identity after init(). Re-resolve every time instead of caching once.
      const el = this.resolveHeaderEl();
      if (el && el !== this.sectionHeader) {
        this.sectionHeader = el;
        this.observeHeader();
      }
      if (!this.sectionHeader) return 0;
      const cl = this.sectionHeader.classList;
      if (cl && (cl.contains('shopify-section-header-hidden') ||
                 cl.contains('bea-mm-hide-up'))) return 0;
      const rect = this.sectionHeader.getBoundingClientRect();
      return Math.max(0, rect.bottom);
    }

    observeHeader() {
      if (this.headerObserver) this.headerObserver.disconnect();
      if (!this.sectionHeader) return;
      this.headerObserver = new MutationObserver(() => {
        this.syncHeaderOffset();
        this.evaluateVisibility();
      });
      this.headerObserver.observe(this.sectionHeader, {
        attributes: true, attributeFilter: ['class', 'style'],
      });
    }

    syncHeaderOffset() {
      this.style.setProperty('--sticky-atc-top-offset', `${this.getHeaderBottom()}px`);
    }

    setupHeaderOffsetSync() {
      this.syncHeaderOffset();

      this.boundScrollHandler = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.syncHeaderOffset();
          this.evaluateVisibility();
          this.scrollFrame = null;
        });
      };

      window.addEventListener('scroll', this.boundScrollHandler, { passive: true });
      window.addEventListener('resize', this.boundScrollHandler);

      // The megamenu pins itself on DOMContentLoaded / load, after we init.
      // Re-measure at those points so the offset picks up the real header.
      document.addEventListener('DOMContentLoaded', () => this.boundScrollHandler());
      window.addEventListener('load', () => this.boundScrollHandler());
      setTimeout(() => this.boundScrollHandler(), 300);
      setTimeout(() => this.boundScrollHandler(), 1000);

      this.observeHeader();
    }

    /* ═══════════════ VISIBILITY ═══════════════ */

    evaluateVisibility() {
      if (!this.triggerEl) return;
      const triggerRect = this.triggerEl.getBoundingClientRect();
      const headerBottom = this.getHeaderBottom();
      const scrolledPast = triggerRect.bottom < headerBottom;
      const triggerInOrBelowViewport = triggerRect.top < window.innerHeight;
      this.dataset.visible = (scrolledPast && triggerInOrBelowViewport) ? 'true' : 'false';
    }

    setupVisibilityLogic() {
      this.observer = new IntersectionObserver(
        () => this.evaluateVisibility(),
        { threshold: [0, 0.5, 1] }
      );
      this.observer.observe(this.triggerEl);
    }

    /* ═══════════════ VARIANT SYNC ═══════════════ */

    findVariantById(id) {
      return this.variants.find(v => String(v.id) === String(id)) || null;
    }

    checkCustomRadiosForVariant(variant) {
      if (!variant || !Array.isArray(variant.options)) return;
      variant.options.forEach((value, i) => {
        const group = this.variantGroups[i];
        if (!group) return;
        const inputs = group.querySelectorAll('input.' + (this.pdpPrefix || 'bea-cdpdp') + '__variant-input');
        let matched = null;
        inputs.forEach(inp => {
          if (String(inp.value).trim() === String(value).trim()) matched = inp;
        });
        if (matched && !matched.checked) {
          matched.checked = true;
          matched.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    syncDawnVariant(variantId) {
      if (this.mainVariantId) {
        this.mainVariantId.value = variantId;
        this.mainVariantId.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const picker = (this.productInfo || document)
        .querySelector('variant-radios, variant-selects');
      if (picker) {
        const input = picker.querySelector(`[value="${variantId}"]`);
        if (input) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    setupVariantSync() {
      if (!this.variantSelect) return;

      this.variantSelect.addEventListener('change', (e) => {
        const variantId = e.target.value;

        if (this.mode === 'custom') {
          const variant = this.findVariantById(variantId);
          if (variant) this.checkCustomRadiosForVariant(variant);
          this.applyVariantToStickyBar(variant);
        } else {
          this.syncDawnVariant(variantId);
          this.dataset.currentVariantId = variantId;
          this.updatePriceAndAvailabilityFromSection(variantId);
        }
      });
    }

    listenForMainFormChanges() {
      if (this.mode === 'custom') {
        this.mainRadios.forEach(input => {
          input.addEventListener('change', () => {
            setTimeout(() => {
              const currentId = this.mainVariantId && this.mainVariantId.value;
              if (!currentId) return;
              this.dataset.currentVariantId = currentId;
              if (this.variantSelect && this.variantSelect.value !== String(currentId)) {
                this.variantSelect.value = currentId;
              }
              this.applyVariantToStickyBar(this.findVariantById(currentId));
            }, 0);
          });
        });
      } else {
        if (!this.mainVariantId) return;
        this.mutationObserver = new MutationObserver(() => {
          const newId = this.mainVariantId.value;
          if (this.variantSelect && this.variantSelect.value !== newId) {
            this.variantSelect.value = newId;
          }
          if (newId) this.dataset.currentVariantId = newId;
          this.updatePriceAndAvailabilityFromSection(newId);
        });
        this.mutationObserver.observe(this.mainVariantId, {
          attributes: true, attributeFilter: ['value'],
        });
      }
    }

    applyVariantToStickyBar(variant) {
      if (!variant) return;
      if (this.priceEl && variant.price) this.priceEl.textContent = variant.price;
      if (this.addButton) this.addButton.disabled = !variant.available;
      if (this.buttonText) {
        const desktop = variant.available ? this.addLabel       : this.soldOutLabel;
        const mobile  = variant.available ? this.addLabelMobile : this.soldOutLabel;
        this.buttonText.innerHTML =
          `<span class="sticky-atc__button-text--desktop">${desktop}</span>` +
          `<span class="sticky-atc__button-text--mobile">${mobile}</span>`;
      }
    }

    async updatePriceAndAvailabilityFromSection(variantId) {
      if (!this.dawnSectionId || !variantId) return;
      try {
        const url = `${window.location.pathname}?variant=${variantId}&section_id=${this.dawnSectionId}`;
        const res = await fetch(url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const newPrice = doc.querySelector(`#price-${this.dawnSectionId}`)
                      || doc.querySelector('.price');
        if (newPrice && this.priceEl) this.priceEl.innerHTML = newPrice.innerHTML;

        const newBtn = doc.querySelector('button[name="add"]');
        if (newBtn && this.addButton && this.buttonText) {
          const available = !newBtn.disabled;
          this.addButton.disabled = !available;
          const desktop = available ? this.addLabel       : this.soldOutLabel;
          const mobile  = available ? this.addLabelMobile : this.soldOutLabel;
          this.buttonText.innerHTML =
            `<span class="sticky-atc__button-text--desktop">${desktop}</span>` +
            `<span class="sticky-atc__button-text--mobile">${mobile}</span>`;
        }
      } catch (err) {
        console.error('Sticky ATC section fetch failed', err);
      }
    }

    /* ═══════════════ QUANTITY ═══════════════ */

    setupQuantityStepper() {
      if (!this.quantityInput) return;

      const clamp = () => {
        let val = parseInt(this.quantityInput.value, 10);
        const min = parseInt(this.quantityInput.min, 10) || 1;
        if (Number.isNaN(val) || val < min) val = min;
        this.quantityInput.value = val;
        return val;
      };

      if (this.quantityMinus) {
        this.quantityMinus.addEventListener('click', () => {
          const min = parseInt(this.quantityInput.min, 10) || 1;
          const val = clamp();
          this.quantityInput.value = Math.max(min, val - 1);
        });
      }
      if (this.quantityPlus) {
        this.quantityPlus.addEventListener('click', () => {
          const val = clamp();
          this.quantityInput.value = val + 1;
        });
      }
      this.quantityInput.addEventListener('change', clamp);
      this.quantityInput.addEventListener('blur',   clamp);
    }

    getQuantity() {
      if (this.quantityInput) {
        const val = parseInt(this.quantityInput.value, 10);
        if (!Number.isNaN(val) && val >= 1) return val;
      }
      return 1;
    }

    resolveVariantId() {
      if (this.variantSelect && this.variantSelect.value) return this.variantSelect.value;
      if (this.mainVariantId && this.mainVariantId.value) return this.mainVariantId.value;
      return this.dataset.currentVariantId || null;
    }

    /* ═══════════════ ADD TO CART ═══════════════ */

    setupAddToCart() {
      if (!this.addButton) return;

      this.addButton.addEventListener('click', async (e) => {
        e.preventDefault();
        if (this.addButton.disabled || this.addButton.getAttribute('aria-busy') === 'true') return;

        const variantId = this.resolveVariantId();
        if (!variantId) {
          console.warn('Sticky ATC: no variant id available');
          return;
        }

        const quantity = this.getQuantity();

        if (this.mainForm && this.mainCta) {
          if (this.mainVariantId) this.mainVariantId.value = variantId;

          let mainQty = this.mainForm.querySelector('input[name="quantity"]');
          if (!mainQty) {
            const formAttrId = this.getFormId(this.mainForm);
            const assoc = formAttrId
              ? document.querySelector(`input[name="quantity"][form="${formAttrId}"]`)
              : null;
            if (assoc) {
              assoc.value = quantity;
            } else {
              mainQty = document.createElement('input');
              mainQty.type = 'hidden';
              mainQty.name = 'quantity';
              this.mainForm.appendChild(mainQty);
              mainQty.value = quantity;
            }
          } else {
            mainQty.value = quantity;
          }

          this.addButton.setAttribute('aria-busy', 'true');
          try {
            if (typeof this.mainForm.requestSubmit === 'function') {
              this.mainForm.requestSubmit(this.mainCta);
            } else {
              this.mainForm.submit();
            }
          } finally {
            setTimeout(() => this.addButton.setAttribute('aria-busy', 'false'), 800);
          }
          return;
        }

        this.addButton.setAttribute('aria-busy', 'true');
        try {
          await this.addToCartAjax(variantId, quantity);
        } catch (err) {
          console.error('Sticky ATC: add to cart failed', err);
        } finally {
          this.addButton.setAttribute('aria-busy', 'false');
        }
      });
    }

    async addToCartAjax(variantId, quantity = 1) {
      const cart = document.querySelector('cart-notification') ||
                   document.querySelector('cart-drawer');

      const formData = { items: [{ id: parseInt(variantId, 10), quantity: quantity }] };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        formData.sections = cart.getSectionsToRender().map(s => s.id);
        formData.sections_url = window.location.pathname;
      }

      const routes = (window.Shopify && window.Shopify.routes) || window.routes || {};
      const addUrl = (routes.cart_add_url) ||
                     ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root ?
                       window.Shopify.routes.root : '/') + 'cart/add');
      const response = await fetch(`${addUrl}.js`.replace('//cart', '/cart'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (!response.ok) {
        if (cart && typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(document.activeElement);
        }
        throw new Error(data.description || data.message || 'Add to cart failed');
      }
      if (cart && typeof cart.renderContents === 'function') {
        cart.renderContents(data);
      } else {
        window.location.href = (routes.cart_url) || '/cart';
      }

      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && data.sections && data.sections['cart-icon-bubble']) {
        const doc = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
        const newBubble = doc.querySelector('#cart-icon-bubble');
        if (newBubble) bubble.innerHTML = newBubble.innerHTML;
      }
    }
  }

  customElements.define('sticky-atc', StickyAtc);
}